/**
 * HeaderDownloadPopup Component
 *
 * Renders the download options popup in the company details header.
 * Provides 5 download options:
 * - Download onepager as PPTX
 * - Download financials as XLSX
 * - Download full profile as PPTX
 * - Download comparables as XLSX
 * - Download contacts as XLSX
 *
 * Each option has permission-based visibility and loading/success states.
 */

import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_TYPES, ICON_BUTTON_ICONS } from '../../../buttons/IconButton';
import EnrichmentCreditCounter from '../../../common/EnrichmentCreditCounter';
import SubscriptionDownloadCounter from '../../../common/SubscriptionDownloadCounter';
import useSubscriptionStatus from '../../../../hooks/useSubscriptionStatus';
import { exportBlockedFor, exportBlockedNode } from '../../../common/exportAccessTooltips';

/**
 * @param {Object} props
 * @param {Function} props.onDownload - Callback when download is clicked, receives download type
 * @param {Function} props.requestBulkExport - Bulk-export gated callback (used for comparables)
 * @param {boolean} props.canExportOnepager - Permission to export onepager
 * @param {boolean} props.canExportFinancials - Permission to export financials
 * @param {boolean} props.canExportFullProfile - Permission to export full profile
 * @param {boolean} props.canExportComparables - Permission to export comparables
 * @param {boolean} props.isDownloadingOnepager - Whether onepager is downloading
 * @param {boolean} props.isDownloadingFinancials - Whether financials is downloading
 * @param {boolean} props.isDownloadingFullProfile - Whether full profile is downloading
 * @param {boolean} props.isDownloadingComparables - Whether comparables is downloading
 * @param {boolean} props.isSuccessOnepager - Whether onepager download succeeded
 * @param {boolean} props.isSuccessFinancials - Whether financials download succeeded
 * @param {boolean} props.isSuccessFullProfile - Whether full profile download succeeded
 * @param {boolean} props.isSuccessComparables - Whether comparables download succeeded
 * @param {boolean} props.canExportContacts - Permission to export contacts
 * @param {boolean} props.isExportDisabled - Whether the caller carries `profilerexportdisabled`
 * @param {boolean} props.isDownloadingContacts - Whether contacts is downloading
 * @param {boolean} props.isSuccessContacts - Whether contacts download succeeded
 * @param {boolean} props.hasContacts - Whether company has contacts
 * @param {boolean} props.isFinancialsDataEmpty - Whether financials data is empty
 * @param {boolean} props.isLoadingDetails - Whether details are loading
 * @param {boolean} props.hasRevenue - Whether company has revenue data
 * @param {boolean} props.similarCompaniesInitialLoad - Whether similar companies initial load is pending
 * @param {boolean} props.isLoadingSimilarCompanies - Whether similar companies are loading
 * @param {string|null} props.loadSimilarCompaniesError - Error from loading similar companies
 * @param {boolean} props.hasSimilarCompanies - Whether there are similar companies
 */
const HeaderDownloadPopup = ({
  onDownload,
  requestBulkExport,
  canExportOnepager = false,
  canExportFinancials = false,
  canExportFullProfile = false,
  canExportComparables = false,
  isDownloadingOnepager = false,
  isDownloadingFinancials = false,
  isDownloadingFullProfile = false,
  isDownloadingComparables = false,
  isSuccessOnepager = false,
  isSuccessFinancials = false,
  isSuccessFullProfile = false,
  isSuccessComparables = false,
  canExportContacts = false,
  isExportDisabled = false,
  isDownloadingContacts = false,
  isSuccessContacts = false,
  hasContacts = true,
  isFinancialsDataEmpty = false,
  isLoadingDetails = false,
  hasRevenue = true,
  similarCompaniesInitialLoad = false,
  isLoadingSimilarCompanies = false,
  loadSimilarCompaniesError = null,
  hasSimilarCompanies = true
}) => {
  const { downloads } = useSubscriptionStatus();
  const isLimitReached = downloads?.limitReached ?? false;

  // TD-2648: this popup now opens for every role, so the `profilerexportdisabled`
  // right is enforced here instead of on the trigger that opens it. Drives both the
  // disabled state and tooltipClickable (the blocked body carries a mailto link).
  const isBlocked = exportBlockedFor(isExportDisabled);

  // Onepager disabled state
  const isOnepagerDisabled = isLimitReached || isBlocked(canExportOnepager) || isDownloadingOnepager;

  // Financials disabled state
  const isFinancialsDisabled =
    isLimitReached ||
    isBlocked(canExportFinancials) ||
    isDownloadingFinancials ||
    isFinancialsDataEmpty ||
    isLoadingDetails;

  // Full profile disabled state
  const isFullProfileDisabled =
    isLimitReached || isBlocked(canExportFullProfile) || isDownloadingFullProfile;

  // Comparables disabled state
  const isComparablesDisabled =
    isLimitReached ||
    isBlocked(canExportComparables) ||
    !hasRevenue ||
    isDownloadingComparables ||
    similarCompaniesInitialLoad ||
    isLoadingSimilarCompanies ||
    loadSimilarCompaniesError ||
    !hasSimilarCompanies;

  // Contacts disabled state
  const isContactsDisabled =
    isLimitReached || isBlocked(canExportContacts) || isDownloadingContacts || !hasContacts;

  // Get download icon based on downloading/success state
  const getDownloadIcon = (isDownloading, isSuccess) => {
    if (isDownloading) return ICON_BUTTON_ICONS.FILE_EARMARK_ARROW_DOWN;
    if (isSuccess) return ICON_BUTTON_ICONS.FILE_EARMARK_ARROW_DOWN_FILL;
    return null;
  };

  // Get onepager tooltip content
  const getOnepagerTooltipContent = () => {
    if (isBlocked(canExportOnepager)) return exportBlockedNode(isExportDisabled);
    if (isDownloadingOnepager) return <>Downloading. Please wait.</>;
    if (isSuccessOnepager) return <>Downloaded successfully</>;
    return null;
  };

  // Get financials tooltip content. TD-2648 replaced the Full-only body with the
  // shared one — the copy no longer names a tier, so the two variants collapsed.
  const getFinancialsTooltipContent = () => {
    if (isBlocked(canExportFinancials)) return exportBlockedNode(isExportDisabled);
    if (isFinancialsDataEmpty) return <>No financials data available for export.</>;
    if (isDownloadingFinancials) return <>Downloading. Please wait.</>;
    if (isSuccessFinancials) return <>Downloaded successfully</>;
    return null;
  };

  // Get full profile tooltip content. TD-2635 opened this export to Profiler Lite,
  // so the only role that still sees the no-access body is Viewer.
  const getFullProfileTooltipContent = () => {
    if (isBlocked(canExportFullProfile)) return exportBlockedNode(isExportDisabled);
    if (isDownloadingFullProfile) return <>Downloading. Please wait.</>;
    if (isSuccessFullProfile) return <>Downloaded successfully</>;
    return null;
  };

  // Get comparables tooltip content
  const getComparablestooltipContent = () => {
    if (isBlocked(canExportComparables)) {
      return exportBlockedNode(isExportDisabled);
    }
    if (!hasRevenue) {
      return 'The selected company has no revenue data. Therefore, no comparable companies are available.';
    }
    if (isDownloadingComparables) return 'Downloading. Please wait.';
    if (isSuccessComparables) return 'Downloaded successfully';
    if (isLoadingSimilarCompanies) return 'Loading comparable companies';
    if (similarCompaniesInitialLoad) return 'Please click on the "comparable companies" tab first';
    if (loadSimilarCompaniesError) return 'Loading comparable companies failed, please try again';
    if (!hasSimilarCompanies) return 'There are no comparable companies available for this company';
    return '';
  };

  // Get contacts tooltip content
  const getContactsTooltipContent = () => {
    if (isBlocked(canExportContacts)) {
      return exportBlockedNode(isExportDisabled);
    }
    if (!hasContacts) return 'No contacts available for this company.';
    if (isDownloadingContacts) return 'Downloading. Please wait.';
    if (isSuccessContacts) return 'Downloaded successfully';
    return '';
  };

  return (
    <div className="financials-download-popup">
      <SubscriptionDownloadCounter label="Downloads" />
      <EnrichmentCreditCounter variant="row" />
      <div className="buttons-wrap">
        <IconButton
          type={ICON_BUTTON_TYPES.BLUE}
          innerText="Download onepager as PPTX"
          withBorder={false}
          onClick={() => onDownload('onepager')}
          disabled={isOnepagerDisabled}
          tooltipClickable={isBlocked(canExportOnepager)}
          tooltipContent={getOnepagerTooltipContent()}
          icon={getDownloadIcon(isDownloadingOnepager, isSuccessOnepager)}
          iconOnRight
        />
        <IconButton
          type={ICON_BUTTON_TYPES.BLUE}
          innerText="Download full profile as PPTX"
          withBorder={false}
          onClick={() => onDownload('full-profile')}
          disabled={isFullProfileDisabled}
          tooltipClickable={isBlocked(canExportFullProfile)}
          tooltipContent={getFullProfileTooltipContent()}
          icon={getDownloadIcon(isDownloadingFullProfile, isSuccessFullProfile)}
          iconOnRight
        />
        <IconButton
          type={ICON_BUTTON_TYPES.BLUE}
          innerText="Download financials as XLSX"
          withBorder={false}
          onClick={() => onDownload('financials')}
          disabled={isFinancialsDisabled}
          tooltipClickable={isBlocked(canExportFinancials)}
          tooltipText={getFinancialsTooltipContent()}
          icon={getDownloadIcon(isDownloadingFinancials, isSuccessFinancials)}
          iconOnRight
        />
        <IconButton
          type={ICON_BUTTON_TYPES.BLUE}
          innerText="Download contacts as XLSX"
          withBorder={false}
          onClick={() => onDownload('contacts')}
          tooltipClickable={isBlocked(canExportContacts)}
          disabled={isContactsDisabled}
          tooltipContent={getContactsTooltipContent()}
          icon={getDownloadIcon(isDownloadingContacts, isSuccessContacts)}
          iconOnRight
        />
        <IconButton
          type={ICON_BUTTON_TYPES.BLUE}
          innerText="Download comparables as XLSX"
          withBorder={false}
          onClick={() => requestBulkExport('comparable-companies')}
          tooltipClickable={isBlocked(canExportComparables)}
          disabled={isComparablesDisabled}
          tooltipContent={getComparablestooltipContent()}
          icon={getDownloadIcon(isDownloadingComparables, isSuccessComparables)}
          iconOnRight
        />
      </div>
    </div>
  );
};

HeaderDownloadPopup.propTypes = {
  onDownload: PropTypes.func.isRequired,
  requestBulkExport: PropTypes.func.isRequired,
  canExportOnepager: PropTypes.bool,
  canExportFinancials: PropTypes.bool,
  canExportFullProfile: PropTypes.bool,
  canExportComparables: PropTypes.bool,
  isDownloadingOnepager: PropTypes.bool,
  isDownloadingFinancials: PropTypes.bool,
  isDownloadingFullProfile: PropTypes.bool,
  isDownloadingComparables: PropTypes.bool,
  isSuccessOnepager: PropTypes.bool,
  isSuccessFinancials: PropTypes.bool,
  isSuccessFullProfile: PropTypes.bool,
  isSuccessComparables: PropTypes.bool,
  canExportContacts: PropTypes.bool,
  isExportDisabled: PropTypes.bool,
  isDownloadingContacts: PropTypes.bool,
  isSuccessContacts: PropTypes.bool,
  hasContacts: PropTypes.bool,
  isFinancialsDataEmpty: PropTypes.bool,
  isLoadingDetails: PropTypes.bool,
  hasRevenue: PropTypes.bool,
  similarCompaniesInitialLoad: PropTypes.bool,
  isLoadingSimilarCompanies: PropTypes.bool,
  loadSimilarCompaniesError: PropTypes.string,
  hasSimilarCompanies: PropTypes.bool
};

export default HeaderDownloadPopup;
